import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import { BodyMedium, BodySmall, TextSize90 } from '@shared/components/core/text/Typography';
import IDetailContentDialogProps from '@shared/models/components/IDetailContentDialogProps';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { TinyIconButton } from '../tiny/TinyIconsButton';
import TinyLabel from '../tiny/TinyLabel';
import {
    DialogTitleWrap,
    DisplayDialogTitle,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTag,
    ThemedDialog,
} from './CoreDialogStyles';

/**
 * A ContentDialog for showing one thing in detail: leading media, a title with a subtitle beneath it, and
 * a row of actions alongside the close button — the shape an extension, skill, or tool detail view wants.
 *
 * It keeps everything ContentDialog does for accessibility: the dialog is labelled by its own title and
 * described by its subtitle and description, the close control carries a real label rather than a bare
 * glyph, and the header actions can be announced as a named group. Where ContentDialog assumes a confirm
 * action, this one treats the footer as optional, since a detail view is often read-only.
 */
export default function DetailContentDialog({
    title,
    titleAdornment,
    subtitle,
    description,
    image,
    headerActions,
    headerActionsLabel,
    onCancel,
    cancelButtonText,
    onConfirm,
    confirmDisabled,
    confirmStartComponent,
    onClose,
    error,
    confirmButtonText,
    confirmIcon,
    cancelIcon,
    children,
    scroll = 'paper',
    dialogTag,
    ...restProps
}: Readonly<IDetailContentDialogProps>) {
    const { t } = useTranslation('common');

    const id = useId();
    const subtitleId = `dialog-subtitle-${id}`;
    const descriptionId = `dialog-description-${id}`;
    const cancelText = cancelButtonText ?? t('cancel');
    const confirmText = confirmButtonText ?? t('save');

    // Both lines describe the dialog, so both are named — the subtitle first, the way they read on screen.
    const describedBy = [subtitle && subtitleId, description && descriptionId].filter(Boolean).join(' ');

    return (
        <ThemedDialog
            scroll={scroll}
            onClose={onClose}
            aria-labelledby={`dialog-title-${id}`}
            aria-describedby={describedBy || undefined}
            {...restProps}>
            <DialogTitleWrap>
                {image && <HeaderMedia>{image}</HeaderMedia>}
                <HeaderText>
                    <TitleRow>
                        <HeaderTitle id={`dialog-title-${id}`}>
                            {dialogTag && <StyledDialogTag as="span">{dialogTag}</StyledDialogTag>}
                            {title}
                        </HeaderTitle>
                        {titleAdornment}
                    </TitleRow>
                    {subtitle && <HeaderSubtitle id={subtitleId}>{subtitle}</HeaderSubtitle>}
                </HeaderText>
                <HeaderActions role={headerActionsLabel ? 'group' : undefined} aria-label={headerActionsLabel}>
                    {headerActions}
                    {onClose && (
                        <TinyIconButton
                            label={t('close')}
                            tooltip={t('close')}
                            tooltipPlacement="bottom"
                            icon="closeX"
                            variant="text"
                            onClick={onClose}
                        />
                    )}
                </HeaderActions>
            </DialogTitleWrap>

            <StyledDialogContent>
                {description && <Description id={descriptionId}>{description}</Description>}
                {children}
            </StyledDialogContent>

            {(onConfirm || onCancel) && (
                <StyledDialogActions>
                    {error && <TinyLabel variant="error" title={error} />}
                    {cancelText && onCancel && (
                        <Button $size="small" $color="simple" startIcon={cancelIcon} onClick={onCancel}>
                            {cancelText}
                        </Button>
                    )}
                    {onConfirm && (
                        <Button
                            $size="small"
                            $color="secondary"
                            startIcon={confirmStartComponent ? undefined : confirmIcon}
                            startComponent={confirmStartComponent}
                            disabled={confirmDisabled}
                            onClick={onConfirm}>
                            {confirmText}
                        </Button>
                    )}
                </StyledDialogActions>
            )}
        </ThemedDialog>
    );
}

/** A fixed square so a tall or wide image can't push the title out of line. */
const HeaderMedia = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
    overflow: hidden;
    color: ${(props) => props.theme.color.accent};
    background: ${(props) => props.theme.color.card};

    img,
    svg {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1 1 auto;
`;

/** Keeps the type tag on the title's line, wrapping beneath it only when the name runs long. */
const TitleRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
`;

/** DisplayDialogTitle centers itself for the single-line header; here it heads a column instead. */
const HeaderTitle = styled(DisplayDialogTitle)`
    align-self: flex-start;
`;

const HeaderSubtitle = styled(BodyMedium)`
    color: ${(props) => props.theme.color.textBody};
    ${TextSize90}
`;

/** Kept on one line beside the close button, so the actions read as a set rather than as stray glyphs. */
const HeaderActions = styled.div`
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
`;

const Description = styled(BodySmall)`
    white-space: pre-wrap;
    color: ${(props) => props.theme.color.textBody};
    font-weight: 300;
`;
